import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { renderMermaidDiagrams } from '../../utils/mermaidRenderer';

/**
 * Reusable Mermaid Preview Component
 *
 * Uses existing mermaidRenderer utility for consistent rendering
 * across the application.
 *
 * @param {string} code - Mermaid diagram code
 * @param {number} zoomLevel - Zoom level (default: 1)
 * @param {string} id - Unique ID for the diagram (optional)
 * @param {object} containerStyle - Custom styles for outer container (optional)
 * @param {number} minHeight - Minimum height of preview (default: 200)
 * @param {number} maxHeight - Maximum height of preview (default: 600)
 */
const MermaidPreview = ({
  code,
  zoomLevel = 1,
  id,
  containerStyle = {},
  minHeight = 200,
  maxHeight = 600
}) => {
  const containerRef = useRef(null);
  const [diagramId] = useState(() => id || `mermaid-preview-${Date.now()}`);
  const [isMounted, setIsMounted] = useState(false);

  // Track when component is mounted
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Render when code changes OR when component mounts (NOT on zoomLevel change)
  // Zoom is handled purely via CSS transform
  useEffect(() => {
    if (!code || !isMounted) return;

    // Use setTimeout to ensure containerRef is attached
    const timer = setTimeout(() => {
      if (!containerRef.current) return;

      const element = containerRef.current.querySelector('.mermaid-diagram');
      if (element) {
        // Reset to unprocessed so it will re-render
        element.setAttribute('data-processed', 'false');
        element.setAttribute('data-code', code);

        // Small delay to ensure DOM is ready
        setTimeout(() => {
          renderMermaidDiagrams(1); // Always render at scale 1, zoom is CSS-based
        }, 150);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [code, isMounted]);

  // Base scale of 4 - zoomLevel adjusts from this base (matching MarkdownWithMermaid)
  // zoomLevel 1 = scale 4, zoomLevel 0.5 = scale 2, zoomLevel 1.5 = scale 6
  const baseScale = 1;
  const effectiveZoom = baseScale * zoomLevel;

  return (
    <Box ref={containerRef}>
      {/* Outer container - matches .accordion-diagram structure for mermaidRenderer */}
      <Box
        className="accordion-diagram"
        sx={{
          padding: '20px',
          backgroundColor: '#f9f9f9',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          minHeight: `${minHeight}px`,
          maxHeight: `${maxHeight}px`,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            borderRadius: '5px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            },
          },
          ...containerStyle
        }}
      >
        {/* Scaling wrapper - applies zoom transform */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            minHeight: '400px',
            padding: '30px',
          }}
        >
          {/* Mermaid diagram container */}
          <div
            id={diagramId}
            className="mermaid-diagram"
            data-code={code}
            data-processed="false"
            style={{
              transform: `scale(${effectiveZoom})`,
              transformOrigin: 'top left',
              transition: 'transform 0.3s ease',
            }}
          >
            {/* Loading state - will be replaced by SVG */}
            <Box
              sx={{
                textAlign: 'center',
                color: '#888',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Box className="spinner" sx={{ width: 20, height: 20 }} />
              <span>Loading diagram...</span>
            </Box>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default MermaidPreview;
